import { useEditMode } from 'purchasers/editMode'
import React, { ReactElement, ReactNode } from 'react'
import { Select, SizableText } from 'tamagui'
import { ChevronDown as CaretDown } from '@tamagui/lucide-icons-2'
import { Label } from './label'

interface OptionProps {
    value: any
    children?: ReactNode
    disabled?: boolean
    [key: string]: any
}

/**
 * Option component for use as children of CustomSelect.
 * This is a data-only component - it doesn't render anything directly.
 * The parent Select reads its props (value, children) to build the select items.
 */
export const Option = (_props: OptionProps): ReactElement => {
    return null as any
}

interface Props {
    value?: any
    defaultValue?: any
    onChange?: (event: any, value: any) => void
    onValueChange?: (value: string) => void
    children?: ReactNode
    multiple?: boolean
    disabled?: boolean
    placeholder?: string
    required?: boolean
    name?: string
    error?: boolean | any
    label?: string
    renderValue?: (value: any) => ReactNode
    renderNewLines?: boolean
    slotProps?: any
    sx?: any
    variant?: any
    color?: any
    size?: any
    [key: string]: any
}

/**
 * Extracts option data from children (Option elements).
 */
function extractOptions(children: ReactNode): { value: any; label: ReactNode; disabled?: boolean }[] {
    const options: { value: any; label: ReactNode; disabled?: boolean }[] = []
    React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
            const childProps = child.props as any
            options.push({
                value: childProps.value,
                label: childProps.children ?? childProps.value,
                disabled: childProps.disabled,
            })
        }
    })
    return options
}

const CustomSelect = ({
    renderNewLines,
    children,
    label,
    editMode: editModeProp,
    ...props
}: Props & { editMode?: boolean }) => {
    const editModeProvided = useEditMode()
    const editMode = editModeProp ?? editModeProvided.editMode
    const options = extractOptions(children)

    if (!editMode) {
        const content = (() => {
            if (props.multiple) {
                const vals = ((props.value as any[]) ?? []).map(value => {
                    const option = options.find(opt => opt.value === value)
                    return option?.label || value
                })

                if (renderNewLines) {
                    return vals.map((x, i) => (
                        <React.Fragment key={i}>
                            <SizableText>{x}</SizableText>
                            {i < vals.length - 1 && <br />}
                        </React.Fragment>
                    ))
                } else {
                    return <SizableText>{vals.join(', ')}</SizableText>
                }
            }
            const option = options.find(opt => opt.value === props.value)
            return <SizableText>{(option?.label || props.value) ?? '\u00A0'}</SizableText>
        })()

        if (label) {
            return (
                <>
                    <Label>{label}</Label>
                    {content}
                </>
            )
        }
        return <>{content}</>
    }

    // For multiple select, Tamagui's Select doesn't natively support multi-select.
    // For now, render as a single select. If multiple is needed, consumers should
    // be updated to use a different pattern.
    const currentValue = props.multiple
        ? String((props.value as any[])?.[0] ?? '')
        : String(props.value ?? '')

    const selectElement = (
        <Select
            value={currentValue}
            onValueChange={(val: string) => {
                if (props.onChange) {
                    props.onChange(null, val)
                }
                if (props.onValueChange) {
                    props.onValueChange(val)
                }
            }}
            disablePreventBodyScroll
        >
            <Select.Trigger
                disabled={props.disabled}
                width="100%"
                height={44}
                borderRadius="$4"
                backgroundColor="$backgroundStrong"
                iconAfter={<CaretDown size={16} />}
                borderColor={props.error ? "$statusDanger" : "$borderColor"}
                focusStyle={{
                    borderColor: props.error ? "$statusDanger" : "$borderColorFocus",
                    outlineWidth: 0,
                }}
                hoverStyle={{
                    borderColor: props.error ? "$statusDanger" : "$borderColorHover",
                }}
                disabledStyle={{
                    opacity: 0.6,
                    cursor: 'not-allowed',
                }}
            >
                <Select.Value placeholder={props.placeholder ?? 'Select...'} />
            </Select.Trigger>

            <Select.Content>
                <Select.Viewport zIndex={200000}>
                    {options.map((option, index) => (
                        <Select.Item
                            key={String(option.value)}
                            value={String(option.value)}
                            index={index}
                            disabled={option.disabled}
                            animation="quick"
                            hoverStyle={{ backgroundColor: '$backgroundHover' }}
                            pressStyle={{ backgroundColor: '$backgroundPress' }}
                        >
                            <Select.ItemText
                                fontWeight={String(option.value) === currentValue ? '600' : '500'}
                            >
                                {option.label}
                            </Select.ItemText>
                        </Select.Item>
                    ))}
                </Select.Viewport>
            </Select.Content>
        </Select>
    )

    if (label) {
        return (
            <>
                <Label>{label}</Label>
                {selectElement}
            </>
        )
    }

    return selectElement
}

export default CustomSelect
